import React, { useContext, useEffect, useMemo, useState } from 'react';

// components
import { Search } from './search';
import { SearchContext } from './search-provider';

// types
import type { FlyoutDisplayMode } from '../locator.types';

// utils
import { useRecentPredictionsStore } from './utils/use-recent-predictions-store';
import { highlightPredictions } from './utils/highlight-predictions';
import { usePredictions } from './utils/use-predictions';
import { useLocatorConfiguration } from '../utils/use-locator-configuration';
import { useTracking as useGA4Tracking } from 'utils/tracking/track';

export function SearchContainer() {
  const recentResults = useRecentPredictionsStore();
  const locatorSearch = useLocatorConfiguration();

  const { findLocation, geoLocated, geoLocationPending, locate, location } =
    useContext(SearchContext);

  const [value, setValue] = useState<string>(location?.address || '');
  const predictions = usePredictions(value);
  const flyoutDisplayMode: FlyoutDisplayMode = value ? 'suggestions' : 'recentResults';
  const track = useGA4Tracking();

  const suggestions = useMemo(() => {
    const hightLightOnlyMainText = flyoutDisplayMode === 'recentResults';

    return highlightPredictions(
      flyoutDisplayMode === 'suggestions' ? predictions : recentResults,
      hightLightOnlyMainText,
    );
  }, [flyoutDisplayMode, predictions, recentResults]);

  const flyoutLabel =
    flyoutDisplayMode === 'suggestions'
      ? locatorSearch?.search?.suggestionsLabel
      : locatorSearch?.search?.recentResultsLabel;

  const iconName = flyoutDisplayMode === 'suggestions' ? 'Location' : 'Clock';

  const handleOnSubmit = async (val: string) => {
    track.trackLocator({
      click_intent: 'locator_search',
      search_term: val,
      currency: 'EUR',
      value: 2,
      content_type: 'locator-partner',
    });
    findLocation(val);
  };

  useEffect(() => {
    setValue(location?.address ?? '');
  }, [location?.geoCoordinates, location?.address]);

  return (
    <Search
      iconName={iconName}
      flyoutLabel={flyoutLabel}
      onSubmit={() => {
        handleOnSubmit(value);
      }}
      onChange={(val: string, final: boolean) => {
        setValue(val);
        if (final) {
          handleOnSubmit(val);
        }
      }}
      onClick={() => track.trackClick({ click_intent: 'locator_tile', click_element: 'tile' })}
      geoLocationPending={geoLocationPending && !geoLocated}
      geoLocated={geoLocated}
      suggestions={suggestions}
      name="zipOrCity"
      placeholder={locatorSearch?.search?.placeholder}
      value={value}
      onLocationClick={locate}
    />
  );
}
